/**
 * Theme configuration for the Internal Dashboard
 * This file contains theme constants used throughout the application
 * for consistent styling, spacing, typography, and colors.
 */

import { createTheme } from '@mui/material/styles';

export const colors = {
  // Primary color from design spec
  primary: {
    main: '#46A8DC',
  },

  // Feedback/Semantic colors from design spec
  success: {
    main: '#28C76F',
  },
  warning: {
    main: '#EAF12D',
  },
  error: {
    main: '#EA5455',
  },
  info: {
    main: '#00CFE8',
  },

  // Light mode colors (basic structure for compatibility)
  text: {
    primary: '#213547',
    secondary: '#6e6e6e',
    disabled: '#a0a0a0',
  },
  background: {
    default: '#ffffff',
    paper: '#f5f5f5',
  },

  // Dark mode colors from design spec
  dark: {
    background: {
      page: '#13172A',
      card: '#13172A', // 4% opacity over #13172A
      layer2: '#FFFFFF', // 8% opacity over #13172A
      popup: '#1A1F33',
      topnav: '#1D1D1D', // 24% opacity with blur
      helper: '#1C2033',
      notificationAlt: '#161928',
      button: {
        primary: '#46A8DC',
        disabled: '#A6A6A6',
      },
    },
    text: {
      heading: '#F0F0F0',
      subtext: '#F2F2F2',
      subheading: '#CCCCCC',
      body: '#CCCCCC',
      paragraph: '#B0B0B0',
      helper: {
        disabled: '#878787',
        enabled: '#D4D2D2',
      },
      label: '#EAEAEA',
      button: {
        onPrimary: '#FFFFFF',
        onSecondary: '#FFFFFF',
        onDisabled: '#F8F8F8',
      },
    },
    stroke: {
      divider: '#3A3A3A',
      textField: '#46A8DC',
      outline: {
        secondary: '#FFFFFF',
        active: '#D8D8D8',
        disabled: '#616060',
      },
    },
    icon: {
      disabled: '#B0B0B0',
      active: '#F2F1F1',
      primary: '#46A8DC',
    },
  },
};

export const typography = {
  fontFamily: 'Myriad Pro, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  fontSize: {
    // Original sizes for reference:
    // h1: '2.5rem',        // 40px
    // h2: '2rem',          // 32px
    // h3: '1.75rem',       // 28px
    // h4: '1.5rem',        // 24px
    // subheading1: '1.25rem', // 20px
    // body1: '1.125rem',   // 18px
    // paragraph: '1rem',   // 16px
    // label: '0.875rem',   // 14px
    // helperText: '0.75rem', // 12px
    // helperTextSmall: '0.625rem', // 10px
    // buttonLarge: '1rem',   // 16px
    // buttonMedium: '0.875rem', // 14px
    // buttonSmall: '0.75rem',   // 12px

    h1: '2.125rem',      // 34px
    h2: '1.75rem',       // 28px
    h3: '1.5rem',        // 24px
    h4: '1.25rem',       // 20px
    subheading1: '1.125rem', // 18px
    body1: '1rem',       // 16px
    paragraph: '0.875rem', // 14px
    label: '0.75rem',    // 12px
    helperText: '0.625rem', // 10px
    helperTextSmall: '0.5rem', // 8px
    buttonLarge: '0.875rem', // 14px
    buttonMedium: '0.75rem', // 12px
    buttonSmall: '0.625rem', // 10px
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

export const zIndex = {
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// Common theme configuration helper
const getCommonThemeConfig = () => ({
  typography: {
    fontFamily: typography.fontFamily,
    // Custom typography variants
    custom: {
      heading4: {
        fontSize: typography.fontSize.h4,
        fontWeight: typography.fontWeight.regular,
      },
      subheading1: {
        fontSize: typography.fontSize.subheading1,
        fontWeight: typography.fontWeight.regular,
      },
      subheading2: {
        fontSize: typography.fontSize.subheading,
        fontWeight: typography.fontWeight.semibold,
      },
      subheading3: {
        fontSize: typography.fontSize.subheading1,
        fontWeight: typography.fontWeight.medium,
      },
      paragraph: {
        fontSize: typography.fontSize.paragraph,
        fontWeight: typography.fontWeight.regular,
      },
      paragraph1: {
        fontSize: typography.fontSize.paragraph,
        fontWeight: typography.fontWeight.semibold,
      },

      label: {
        fontSize: typography.fontSize.label,
        fontWeight: typography.fontWeight.regular,
      },
      helperText: {
        fontSize: typography.fontSize.helperText,
        fontWeight: typography.fontWeight.regular,
      },
      helperTextSmall: {
        fontSize: typography.fontSize.helperTextSmall,
        fontWeight: typography.fontWeight.regular,
      },
      buttonLarge: {
        fontSize: typography.fontSize.buttonLarge,
        fontWeight: typography.fontWeight.semibold,
      },
      buttonMedium: {
        fontSize: typography.fontSize.buttonMedium,
        fontWeight: typography.fontWeight.semibold,
      },
      buttonSmall: {
        fontSize: typography.fontSize.buttonSmall,
        fontWeight: typography.fontWeight.semibold,
      },
      avatar: {
        fontSize: typography.fontSize.body1,
        fontWeight: typography.fontWeight.semibold,
      },
    },
  },
  spacing: (factor) => `${factor * 8}px`,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
      mobile: 768, // Mobile breakpoint
    },
  },
  components: {
    // Mobile-optimized button component
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 48, // Touch-friendly minimum height
          fontSize: '16px', // Prevent zoom on iOS
          '@media (max-width: 768px)': {
            minHeight: 48,
            padding: '12px 24px',
            fontSize: '16px',
          },
        },
      },
    },
    // Mobile-optimized text field
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            '& .MuiInputBase-input': {
              fontSize: '16px', // Prevent zoom on iOS
            },
          },
        },
      },
    },
    // Mobile-optimized card
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            margin: '8px',
            borderRadius: '12px',
          },
        },
      },
    },
  },
  zIndex: zIndex,
});


// Create dynamic MUI theme based on dark mode preference
export const createDynamicTheme = (isDarkMode = true) => {
  return createTheme({
    palette: /** @type {any} */ ({
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: colors.primary.main,
        contrastText: isDarkMode ? colors.dark.text.button.onPrimary : '#ffffff',
      },
      success: {
        main: colors.success.main,
        contrastText: '#ffffff',
      },
      warning: {
        main: colors.warning.main,
        contrastText: '#000000',
      },
      error: {
        main: colors.error.main,
        contrastText: '#ffffff',
      },
      info: {
        main: colors.info.main,
        contrastText: '#ffffff',
      },
      text: isDarkMode ? {
        primary: colors.dark.text.heading,
        secondary: colors.dark.text.subheading,
        disabled: colors.dark.text.helper.disabled,
      } : colors.text,
      background: isDarkMode ? {
        default: colors.dark.background.page,
        paper: colors.dark.background.card,
        topnav: colors.dark.background.topnav,
      } : colors.background,
      divider: isDarkMode ? colors.dark.stroke.divider : '#e0e0e0',
      // Custom palette properties
      custom: isDarkMode ? {
        background: {
          page: colors.dark.background.page,
          layer2: colors.dark.background.layer2,
          popup: colors.dark.background.popup,
          topnav: colors.dark.background.topnav,
          helper: colors.dark.background.helper,
          card: colors.dark.background.card,
          notificationAlt: colors.dark.background.notificationAlt,
        },
        button: {
          primary: colors.dark.background.button.primary,
          disabled: colors.dark.background.button.disabled,
        },
        text: {
          body: colors.dark.text.body,
          heading: colors.dark.text.heading,
          subtext: colors.dark.text.subtext,
          subheading: colors.dark.text.subheading,
          disabled: colors.dark.text.helper.disabled,
          paragraph: colors.dark.text.paragraph,
          label: colors.dark.text.label,
          helperEnabled: colors.dark.text.helper.enabled,
          helperDisabled: colors.dark.text.helper.disabled,
          buttonOnPrimary: colors.dark.text.button.onPrimary,
          buttonOnSecondary: colors.dark.text.button.onSecondary,
          buttonOnDisabled: colors.dark.text.button.onDisabled,
        },
        stroke: {
          divider: colors.dark.stroke.divider,
          outlineSecondary: colors.dark.stroke.outline.secondary,
          outlineActive: colors.dark.stroke.outline.active,
          outlineDisabled: colors.dark.stroke.outline.disabled,
          textField: colors.dark.stroke.textField,
        },
        icon: {
          disabled: colors.dark.icon.disabled,
          active: colors.dark.icon.active,
          primary: colors.dark.icon.primary,
        },
      } : {
        background: {
          layer2: colors.background.paper,
          popup: colors.background.default,
          topnav: colors.background.default,
          helper: colors.background.paper,
          notificationAlt: colors.background.paper,
        },
        button: {
          primary: colors.primary.main,
          disabled: colors.text.disabled,
        },
        text: {
          body: colors.text.primary,
          paragraph: colors.text.secondary,
          label: colors.text.primary,
          helperEnabled: colors.text.secondary,
          helperDisabled: colors.text.disabled,
          buttonOnPrimary: '#ffffff',
          buttonOnSecondary: '#ffffff',
          buttonOnDisabled: colors.text.disabled,
        },
        stroke: {
          outlineSecondary: '#e0e0e0',
          outlineActive: '#333333',
          outlineDisabled: '#cccccc',
        },
        icon: {
          disabled: colors.text.disabled,
          active: colors.text.primary,
          primary: colors.primary.main,
        },
      },
    }),
    ...getCommonThemeConfig(),
  });
};


// Default export is the dynamic theme (dark mode by default)
export default createDynamicTheme(true);
